/**
 * In-memory mock realtime backend. Speaks the REAL WS envelope catalog from
 * @sinyal/contracts (ServerToClientFrame / ClientToServerFrame) so wsClient and
 * the UI exercise genuine wire shapes — no plaintext crosses except inside the
 * sealed `ciphertext` (see codec.ts). Swapping this for a real Durable-Object
 * WebSocket is a drop-in replacement of `wsClient`'s transport.
 */

import type {
  ClientToServerFrame,
  MsgAckFrame,
  MsgDeliveredServerFrame,
  MsgNewFrame,
  MsgReadServerFrame,
  PresenceUpdateFrame,
  ServerToClientFrame,
  TypingStartServerFrame,
  TypingStopServerFrame,
  WsStoredMessage,
} from '@sinyal/contracts';
import { WS_PROTOCOL_VERSION } from '@sinyal/contracts';
import { seal, open } from './codec';
import { ulid } from './ids';
import { ME, USERS, MESSAGE_HISTORY, CONVERSATIONS } from './mockData';

type Listener = (frame: ServerToClientFrame) => void;

/** Per-conversation server seq counter, seeded from history. */
const seqByConv = new Map<string, number>();
for (const [cid, msgs] of Object.entries(MESSAGE_HISTORY)) {
  seqByConv.set(cid, msgs.reduce((m, x) => Math.max(m, x.seq ?? 0), 0));
}

function nextSeq(cid: string): number {
  const n = (seqByConv.get(cid) ?? 0) + 1;
  seqByConv.set(cid, n);
  return n;
}

/** Pick a non-me member to act as the "remote" actor for a conversation. */
function peerFor(cid: string): string | null {
  const conv = CONVERSATIONS.find((c) => c.id === cid);
  if (!conv) return null;
  const other = conv.members.find((m) => m.userId !== ME.id);
  return other?.userId ?? null;
}

export class MockTransport {
  private listeners = new Set<Listener>();
  private autoReplyTimers = new Set<ReturnType<typeof setTimeout>>();

  onFrame(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(frame: ServerToClientFrame): void {
    for (const l of this.listeners) l(frame);
  }

  /** Begin a session: emit `conn.ready` and a presence burst (simulated). */
  connect(): void {
    setTimeout(() => {
      this.emit({
        t: 'conn.ready',
        v: WS_PROTOCOL_VERSION,
        id: ulid(),
        ts: Date.now(),
        server_time: Date.now(),
        resume_token: ulid(),
        user_id: ME.id,
      });
      // Seed presence for online users.
      for (const u of USERS) {
        const f: PresenceUpdateFrame = {
          t: 'presence.update',
          v: WS_PROTOCOL_VERSION,
          id: ulid(),
          ts: Date.now(),
          user_id: u.id,
          state: u.presence === 'online' ? 'online' : 'offline',
          last_seen_at_ms: u.lastSeenAtMs,
        };
        this.emit(f);
      }
    }, 250);
  }

  /** Accept a client frame and simulate the server's response stream. */
  send(frame: ClientToServerFrame): void {
    switch (frame.t) {
      case 'msg.send':
        this.handleSend(frame);
        break;
      // delivered/read/typing/presence/ping are accepted silently or pong'd.
      case 'ping':
        this.emit({ t: 'pong', v: WS_PROTOCOL_VERSION, id: ulid(), ts: Date.now() });
        break;
      default:
        break;
    }
  }

  private handleSend(frame: Extract<ClientToServerFrame, { t: 'msg.send' }>): void {
    const cid = frame.cid;
    const seq = nextSeq(cid);
    const serverTs = Date.now();

    // 1) Ack the sender (S→C MsgAckFrame).
    const ack: MsgAckFrame = {
      t: 'msg.ack',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: serverTs,
      cid,
      client_msg_id: frame.client_msg_id,
      seq,
      server_ts: serverTs,
    };
    setTimeout(() => this.emit(ack), 220);

    const peer = peerFor(cid);
    if (!peer) return;

    // 2) Simulated delivered then read receipts from the peer.
    const delivered: MsgDeliveredServerFrame = {
      t: 'msg.delivered',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
      up_to_seq: seq,
      user_id: peer,
    };
    setTimeout(() => this.emit(delivered), 900);

    const read: MsgReadServerFrame = {
      t: 'msg.read',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
      up_to_seq: seq,
      user_id: peer,
    };
    setTimeout(() => this.emit(read), 2200);

    // 3) Auto-reply: typing… then a peer message (so receipts feel alive).
    const body = open(frame.ciphertext);
    this.scheduleAutoReply(cid, peer, body.text);
  }

  private scheduleAutoReply(cid: string, peer: string, incomingText: string): void {
    const typingStart: TypingStartServerFrame = {
      t: 'typing.start',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
      user_id: peer,
    };
    const t1 = setTimeout(() => this.emit(typingStart), 2600);
    this.autoReplyTimers.add(t1);

    const replyText = pickReply(incomingText);
    const t2 = setTimeout(() => {
      const typingStop: TypingStopServerFrame = {
        t: 'typing.stop',
        v: WS_PROTOCOL_VERSION,
        id: ulid(),
        ts: Date.now(),
        cid,
        user_id: peer,
      };
      this.emit(typingStop);

      const stored: WsStoredMessage = {
        seq: nextSeq(cid),
        sender_id: peer,
        client_msg_id: ulid(),
        content_type: 'text',
        ciphertext: seal({ text: replyText, content_type: 'text' }),
        ratchet_header: seal({ text: '', content_type: 'system' }), // mocked header
        server_ts: Date.now(),
      };
      const newFrame: MsgNewFrame = {
        t: 'msg.new',
        v: WS_PROTOCOL_VERSION,
        id: ulid(),
        ts: Date.now(),
        cid,
        ...stored,
      };
      this.emit(newFrame);
    }, 4200);
    this.autoReplyTimers.add(t2);
  }

  dispose(): void {
    for (const t of this.autoReplyTimers) clearTimeout(t);
    this.autoReplyTimers.clear();
    this.listeners.clear();
  }
}

const CANNED = [
  'Got it 👍',
  'Nice, that works for me',
  'Haha 😄',
  'Sounds good!',
  'Let me check and get back to you',
  'On it 🚀',
  'Perfect',
  '👀',
];

function pickReply(_incoming: string): string {
  return CANNED[Math.floor(Math.random() * CANNED.length)] as string;
}

export const mockTransport = new MockTransport();
